import React, { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import Base, { ContentWidth } from "../components/basic/Base";
import Button from "../components/basic/Button";
import NumberField from "../components/basic/NumberField";
import SectionHeading from "../components/basic/SectionHeading";
import SegmentedControl from "../components/basic/SegmentedControl";
import WizardSteps from "../components/basic/WizardSteps";
import { Theme } from "../theme";
import {
  spacing,
  fontSize,
  fontWeight,
  green,
  grey,
  darkGrey,
  mediumGrey,
  black,
  white,
} from "../theme";
import {
  type CyclingTemplate,
  type ExperienceLevel,
  type IntervalSport,
  type IntervalTemplate,
  type RaceDistanceId,
  type RunFocus,
  type RunningTemplate,
  type TrainingGoal,
  CYCLING_GENERAL_TIPS,
  GOALS_BY_SPORT,
  INTERVAL_SPORT_LABELS,
  RACE_DISTANCES,
  RUN_FEEL_TABLE,
  RUN_FOCUS_OPTIONS,
  RUNNING_GENERAL_TIPS,
  TRAINING_GOAL_META,
  buildPersonalRunPaces,
  formatCyclingSessionSummary,
  formatPaceMinPerKm,
  formatPaceRange,
  formatRunningSessionSummary,
  getTemplateById,
  goalZoneHint,
  hmsToSec,
  isExperienceLevel,
  isIntervalSport,
  isRaceDistanceId,
  isRunFocus,
  isTrainingGoal,
  paceRangeForKey,
  recommendedTemplateId,
  secToHms,
  templatesForGoal,
  wattRangeLabel,
  wattsFromFtp,
} from "../lib/intervalCalculator";

const WIZARD_STEPS = [
  { number: 1, label: "Förutsättningar" },
  { number: 2, label: "Träningsmål" },
  { number: 3, label: "Ditt pass" },
] as const;
const WIZARD_MIN = 1;
const WIZARD_MAX = WIZARD_STEPS.length;

const EXPERIENCE_OPTIONS: { id: ExperienceLevel; label: string }[] = [
  { id: "beginner", label: "Nybörjare" },
  { id: "intermediate", label: "Motionär" },
  { id: "advanced", label: "Erfaren" },
];

function clampFtp(n: number): number {
  if (!Number.isFinite(n)) return 200;
  return Math.max(80, Math.min(500, Math.round(n)));
}

function defaultRaceTime(id: RaceDistanceId): number {
  return (
    RACE_DISTANCES.find((d) => d.id === id)?.defaultTimeSec ?? 25 * 60
  );
}

function isCyclingTemplate(t: IntervalTemplate): t is CyclingTemplate {
  return t.sport === "cycling";
}

function isRunningTemplate(t: IntervalTemplate): t is RunningTemplate {
  return t.sport === "running";
}

const IntervalCalculatorPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStep = useMemo(() => {
    const raw = Number(searchParams.get("step") || "1");
    if (!Number.isFinite(raw)) return 1;
    return Math.max(WIZARD_MIN, Math.min(WIZARD_MAX, Math.round(raw)));
  }, [searchParams]);

  const sport: IntervalSport = useMemo(() => {
    const raw = searchParams.get("sport") || "cycling";
    return isIntervalSport(raw) ? raw : "cycling";
  }, [searchParams]);

  const ftp = useMemo(() => {
    const raw = Number(searchParams.get("ftp") || "250");
    return clampFtp(raw);
  }, [searchParams]);

  const raceId: RaceDistanceId = useMemo(() => {
    const raw = searchParams.get("race") || "10k";
    return isRaceDistanceId(raw) ? raw : "10k";
  }, [searchParams]);

  const raceTimeSec = useMemo(() => {
    const raw = Number(searchParams.get("rt"));
    if (Number.isFinite(raw) && raw >= 60) return Math.round(raw);
    return defaultRaceTime(raceId);
  }, [searchParams, raceId]);

  const raceHms = useMemo(() => secToHms(raceTimeSec), [raceTimeSec]);

  const runFocus: RunFocus = useMemo(() => {
    const raw = searchParams.get("focus") || "general";
    return isRunFocus(raw) ? raw : "general";
  }, [searchParams]);

  const experience: ExperienceLevel = useMemo(() => {
    const raw = searchParams.get("exp") || "intermediate";
    return isExperienceLevel(raw) ? raw : "intermediate";
  }, [searchParams]);

  const goal: TrainingGoal = useMemo(() => {
    const raw = searchParams.get("goal") || "vo2max";
    if (!isTrainingGoal(raw)) return "vo2max";
    if (!GOALS_BY_SPORT[sport].includes(raw)) return "vo2max";
    return raw;
  }, [searchParams, sport]);

  const templateId = useMemo(() => {
    const raw = searchParams.get("pass");
    const t = raw ? getTemplateById(raw) : undefined;
    if (t && t.sport === sport) return t.id;
    return recommendedTemplateId(goal, sport, runFocus);
  }, [searchParams, goal, sport, runFocus]);

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => {
            if (value == null || value === "") next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const goToStep = useCallback(
    (step: number) => {
      const clamped = Math.max(WIZARD_MIN, Math.min(WIZARD_MAX, step));
      patchParams({ step: clamped === 1 ? null : String(clamped) });
    },
    [patchParams]
  );

  const setSport = (next: IntervalSport) => {
    const nextGoal = GOALS_BY_SPORT[next].includes(goal) ? goal : "vo2max";
    patchParams({
      sport: next === "cycling" ? null : next,
      goal: nextGoal === "vo2max" ? null : nextGoal,
      pass: recommendedTemplateId(nextGoal, next, runFocus),
      step: null,
    });
  };

  const setFtp = (next: number) => {
    patchParams({ ftp: String(clampFtp(next)) });
  };

  const setRaceId = (next: RaceDistanceId) => {
    patchParams({
      race: next === "10k" ? null : next,
      rt: String(defaultRaceTime(next)),
    });
  };

  const setRaceHms = (part: "h" | "m" | "s", value: number) => {
    const next = {
      h: part === "h" ? value : raceHms.h,
      m: part === "m" ? value : raceHms.m,
      s: part === "s" ? value : raceHms.s,
    };
    patchParams({ rt: String(hmsToSec(next.h, next.m, next.s)) });
  };

  const setRunFocus = (next: RunFocus) => {
    patchParams({
      focus: next === "general" ? null : next,
      pass: recommendedTemplateId(goal, "running", next),
    });
  };

  const setExperience = (next: ExperienceLevel) => {
    patchParams({ exp: next === "intermediate" ? null : next });
  };

  const setGoal = (next: TrainingGoal) => {
    patchParams({
      goal: next === "vo2max" ? null : next,
      pass: recommendedTemplateId(next, sport, runFocus),
    });
  };

  const setTemplate = (id: string) => {
    const def = recommendedTemplateId(goal, sport, runFocus);
    patchParams({ pass: id === def ? null : id });
  };

  const runPaces = useMemo(
    () =>
      sport === "running" ? buildPersonalRunPaces(raceId, raceTimeSec) : null,
    [sport, raceId, raceTimeSec]
  );

  const goalTemplates = useMemo(
    () => templatesForGoal(goal, sport),
    [goal, sport]
  );

  const selectedTemplate = useMemo(() => {
    const t =
      getTemplateById(templateId) ||
      getTemplateById(recommendedTemplateId(goal, sport, runFocus));
    return t && t.sport === sport ? t : undefined;
  }, [templateId, goal, sport, runFocus]);

  const summary = useMemo(() => {
    if (!selectedTemplate) return null;
    if (isCyclingTemplate(selectedTemplate)) {
      return formatCyclingSessionSummary(selectedTemplate, experience, ftp);
    }
    if (isRunningTemplate(selectedTemplate) && runPaces) {
      return formatRunningSessionSummary(
        selectedTemplate,
        experience,
        runPaces
      );
    }
    return null;
  }, [selectedTemplate, experience, ftp, runPaces]);

  const canContinueFrom1 =
    sport === "cycling"
      ? ftp >= 80
      : raceTimeSec >= 60 && runPaces != null;

  return (
    <PageWrap>
      <Inner contentWidth={ContentWidth.Wide}>
        <Hero>
          <HeroMedia
            src={`${process.env.PUBLIC_URL || ""}/hero/interval-calc-cycling.jpg`}
            alt=""
            aria-hidden
          />
          <HeroScrim />
          <HeroContent>
            <HeroKicker>Trirebels intervallverktyg</HeroKicker>
            <HeroTitle>Bygg ditt intervallpass</HeroTitle>
            <HeroLead>
              Välj sport och träningsmål — få ett konkret intervallpass med watt
              eller tempo i min/km.
            </HeroLead>
            <GuideLink to="/intervall-guide">
              Läs lathunden: zoner och träningseffekter
            </GuideLink>
          </HeroContent>
        </Hero>

        <StepsBar>
          <WizardSteps
            steps={WIZARD_STEPS.slice()}
            currentStep={currentStep}
            onStepChange={goToStep}
          />
        </StepsBar>

        {currentStep === 1 && (
          <>
            <Card aria-labelledby="int-sport-heading">
              <SectionHeading id="int-sport-heading">
                Sport och utgångspunkt
              </SectionHeading>
              <FieldBlock>
                <SegmentedControl<IntervalSport>
                  label="Sport"
                  options={[
                    { id: "cycling", label: INTERVAL_SPORT_LABELS.cycling },
                    { id: "running", label: INTERVAL_SPORT_LABELS.running },
                  ]}
                  value={sport}
                  onChange={setSport}
                />
              </FieldBlock>

              {sport === "cycling" ? (
                <FieldBlock>
                  <NumberField
                    label="Din FTP (watt)"
                    value={ftp}
                    min={80}
                    max={500}
                    step={5}
                    onChange={setFtp}
                    suffix="W"
                  />
                </FieldBlock>
              ) : (
                <>
                  <FieldBlock>
                    <SegmentedControl<RaceDistanceId>
                      label="Vilket resultat utgår vi från?"
                      options={RACE_DISTANCES.map((d) => ({
                        id: d.id,
                        label: d.label,
                      }))}
                      value={raceId}
                      onChange={setRaceId}
                    />
                  </FieldBlock>
                  <FieldBlock>
                    <TimeLabel>Din tid</TimeLabel>
                    <TimeRow>
                      <NumberField
                        label="Tim"
                        value={raceHms.h}
                        min={0}
                        max={12}
                        onChange={(n) => setRaceHms("h", n)}
                      />
                      <NumberField
                        label="Min"
                        value={raceHms.m}
                        min={0}
                        max={59}
                        onChange={(n) => setRaceHms("m", n)}
                      />
                      <NumberField
                        label="Sek"
                        value={raceHms.s}
                        min={0}
                        max={59}
                        onChange={(n) => setRaceHms("s", n)}
                      />
                    </TimeRow>
                  </FieldBlock>
                  <FieldBlock>
                    <SegmentedControl<RunFocus>
                      label="Vad vill du främst förbättra?"
                      options={RUN_FOCUS_OPTIONS}
                      value={runFocus}
                      onChange={setRunFocus}
                    />
                  </FieldBlock>
                </>
              )}

              <FieldBlock>
                <SegmentedControl<ExperienceLevel>
                  label="Erfarenhet av intervaller"
                  options={EXPERIENCE_OPTIONS}
                  value={experience}
                  onChange={setExperience}
                />
              </FieldBlock>
            </Card>
            <WizardNav>
              <NavSpacer />
              <Button
                as={Theme.Dark}
                disabled={!canContinueFrom1}
                onClick={() => goToStep(2)}
              >
                Nästa: träningsmål
              </Button>
            </WizardNav>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Card aria-labelledby="int-goal-heading">
              <SectionHeading id="int-goal-heading">
                Välj träningsmål
              </SectionHeading>
              <GoalGrid>
                {GOALS_BY_SPORT[sport].map((g) => {
                  const meta = TRAINING_GOAL_META[g];
                  const active = goal === g;
                  return (
                    <GoalCard
                      key={g}
                      type="button"
                      onClick={() => setGoal(g)}
                      css={active ? goalCardActive : undefined}
                      aria-pressed={active}
                    >
                      <GoalTitle>{meta.label}</GoalTitle>
                      <GoalDesc>{meta.description}</GoalDesc>
                      <GoalZone>{goalZoneHint(g, sport)}</GoalZone>
                    </GoalCard>
                  );
                })}
              </GoalGrid>
            </Card>
            <WizardNav>
              <Button as={Theme.Transparent} onClick={() => goToStep(1)}>
                Tillbaka
              </Button>
              <Button as={Theme.Dark} onClick={() => goToStep(3)}>
                Nästa: se passet
              </Button>
            </WizardNav>
          </>
        )}

        {currentStep === 3 && selectedTemplate && summary && (
          <>
            <Card aria-labelledby="int-session-heading">
              <SectionHeading id="int-session-heading">
                Ditt rekommenderade pass
              </SectionHeading>

              <SessionHero>
                <SessionName>{summary.title}</SessionName>
                <SessionEffect>{summary.effect}</SessionEffect>
                <StatGrid>
                  <Stat>
                    <StatLabel>Upplägg</StatLabel>
                    <StatValue>{summary.structure}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Intensitet</StatLabel>
                    <StatValue>{summary.intensity}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>{summary.targetLabel}</StatLabel>
                    <StatValue css={statAccent}>{summary.target}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Vila</StatLabel>
                    <StatValue>{summary.rest}</StatValue>
                  </Stat>
                </StatGrid>
              </SessionHero>

              <AltTitle>Alternativa pass för samma mål</AltTitle>
              <AltList>
                {goalTemplates.map((t) => {
                  const active = t.id === selectedTemplate.id;
                  let metaLine = "";
                  if (isCyclingTemplate(t)) {
                    metaLine = `${t.intensityPctMin}–${t.intensityPctMax} % · ${wattRangeLabel(ftp, t.intensityPctMin, t.intensityPctMax)}`;
                  } else if (runPaces) {
                    const range = paceRangeForKey(runPaces, t.paceKey);
                    metaLine = `${t.intensityLabel} · ${formatPaceRange(range.min, range.max)}`;
                  } else {
                    metaLine = t.intensityLabel;
                  }
                  return (
                    <AltRow
                      key={t.id}
                      type="button"
                      onClick={() => setTemplate(t.id)}
                      css={active ? altRowActive : undefined}
                      aria-pressed={active}
                    >
                      <div>
                        <AltName>{t.name}</AltName>
                        <AltMeta>{metaLine}</AltMeta>
                      </div>
                      <AltPick>{active ? "Valt" : "Välj"}</AltPick>
                    </AltRow>
                  );
                })}
              </AltList>
            </Card>

            {sport === "running" && runPaces ? (
              <Card>
                <SectionHeading>Dina farter i min/km</SectionHeading>
                <PaceTable>
                  <PaceRow>
                    <PaceName>1500–3000 m</PaceName>
                    <PaceVal>
                      {formatPaceRange(runPaces.economyMin, runPaces.economyMax)}
                    </PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>3–5 km</PaceName>
                    <PaceVal>
                      {formatPaceRange(
                        runPaces.threeToFiveMin,
                        runPaces.threeToFiveMax
                      )}
                    </PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>5 km-fart</PaceName>
                    <PaceVal>{formatPaceMinPerKm(runPaces.fiveK)} /km</PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>10 km-fart</PaceName>
                    <PaceVal>{formatPaceMinPerKm(runPaces.tenK)} /km</PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>Tröskel</PaceName>
                    <PaceVal>
                      {formatPaceRange(
                        runPaces.thresholdMin,
                        runPaces.thresholdMax
                      )}
                    </PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>Tröskel / strax under</PaceName>
                    <PaceVal>
                      {formatPaceMinPerKm(runPaces.thresholdEasy)} /km
                    </PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>Maratonfart</PaceName>
                    <PaceVal>
                      {formatPaceMinPerKm(runPaces.marathon)} /km
                    </PaceVal>
                  </PaceRow>
                  <PaceRow>
                    <PaceName>Lätt distans</PaceName>
                    <PaceVal>{formatPaceMinPerKm(runPaces.easy)} /km</PaceVal>
                  </PaceRow>
                </PaceTable>

                <FeelTitle>Känsla / RPE</FeelTitle>
                <FeelTable>
                  {RUN_FEEL_TABLE.map((row) => (
                    <FeelRow key={row.label}>
                      <FeelName>{row.label}</FeelName>
                      <FeelDesc>
                        {row.feel} · RPE {row.rpe}
                      </FeelDesc>
                    </FeelRow>
                  ))}
                </FeelTable>
              </Card>
            ) : null}

            <Card>
              <SectionHeading>Tips</SectionHeading>
              <TipList>
                {selectedTemplate.tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
                {(sport === "running"
                  ? RUNNING_GENERAL_TIPS
                  : CYCLING_GENERAL_TIPS
                ).map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </TipList>
            </Card>

            {sport === "cycling" ? (
              <Card>
                <SectionHeading>Watt från procent</SectionHeading>
                <QuickCalc>
                  {[90, 95, 100, 105, 110, 120, 130, 150].map((pct) => (
                    <QuickChip key={pct}>
                      <QuickPct>{pct} %</QuickPct>
                      <QuickW>{wattsFromFtp(ftp, pct)} W</QuickW>
                    </QuickChip>
                  ))}
                </QuickCalc>
              </Card>
            ) : null}

            <WizardNav>
              <Button as={Theme.Transparent} onClick={() => goToStep(2)}>
                Tillbaka
              </Button>
              <Button as={Theme.Dark} onClick={() => goToStep(1)}>
                Börja om
              </Button>
            </WizardNav>
          </>
        )}
      </Inner>
    </PageWrap>
  );
};

export default IntervalCalculatorPage;

const PageWrap = styled.div({
  padding: { xy: spacing.xxxl },
  backgroundColor: white,
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.m, y: spacing.xl },
  }),
});

const Inner = styled(Base, {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xl,
});

const Hero = styled.header({
  position: "relative",
  borderRadius: "16px",
  color: white,
  backgroundColor: black,
  overflow: "hidden",
  minHeight: "280px",
  ...media({ maxWidth: "767px" }, {
    borderRadius: "12px",
    minHeight: "240px",
  }),
});

const HeroMedia = styled.img({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
});

const HeroScrim = styled.div({
  position: "absolute",
  inset: 0,
  backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(16,112,85,0.55) 70%, rgba(28,133,103,0.35) 100%)`,
  pointerEvents: "none",
});

const HeroContent = styled.div({
  position: "relative",
  zIndex: 1,
  padding: { x: spacing.xxl, y: spacing.xxl },
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.l, y: spacing.xl },
  }),
});

const HeroKicker = styled.span({
  display: "inline-block",
  fontSize: fontSize.xs,
  fontWeight: fontWeight.heavy,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: white,
  backgroundColor: "rgba(255,255,255,0.12)",
  padding: { x: spacing.s, y: "6px" },
  borderRadius: "999px",
  marginBottom: spacing.m,
});

const HeroTitle = styled.h1({
  fontSize: fontSize.hero,
  fontWeight: fontWeight.heavy,
  lineHeight: 1.05,
  color: white,
  margin: { xy: 0 },
  marginBottom: spacing.m,
  maxWidth: "820px",
  ...media({ maxWidth: "767px" }, {
    fontSize: fontSize.xxl,
  }),
});

const HeroLead = styled.p({
  fontSize: fontSize.ml,
  lineHeight: "1.6",
  color: white,
  maxWidth: "760px",
  margin: { xy: 0 },
  marginBottom: spacing.m,
  ...media({ maxWidth: "767px" }, {
    fontSize: fontSize.m,
  }),
});

const GuideLink = styled(Link, {
  display: "inline-block",
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: white,
  textDecoration: "underline",
  ":link": { color: white },
  ":visited": { color: white },
  ":hover": { color: white },
  ":active": { color: white },
});

const StepsBar = styled.div({
  backgroundColor: white,
  borderRadius: "12px",
  padding: { x: spacing.l, y: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  marginBottom: spacing.m,
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.s, y: spacing.s },
    borderRadius: "10px",
  }),
});

const Card = styled.section({
  backgroundColor: mediumGrey,
  borderRadius: "12px",
  padding: { xy: spacing.l },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  ...media({ maxWidth: "767px" }, {
    padding: { xy: spacing.m },
    borderRadius: "10px",
  }),
});

const FieldBlock = styled.div({
  marginTop: spacing.l,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const TimeLabel = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
});

const TimeRow = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.m,
});

const WizardNav = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.m,
  padding: { x: 0, y: spacing.m },
  flexWrap: "wrap",
  ...media({ maxWidth: "520px" }, {
    flexDirection: "column-reverse",
    alignItems: "stretch",
  }),
});

const NavSpacer = styled.div({
  flex: { grow: "1", shrink: "0", basis: "0" },
});

const GoalGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: spacing.m,
  marginTop: spacing.m,
  ...media({ maxWidth: "767px" }, {
    gridTemplateColumns: "1fr",
  }),
});

const GoalCard = styled.button({
  textAlign: "left",
  cursor: "pointer",
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "2px" } },
  borderRadius: "12px",
  padding: { xy: spacing.m },
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  transitionProperty: "border-color, box-shadow",
  transitionDuration: "0.15s",
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "2px" } },
  },
});

const goalCardActive = styled({
  border: { xy: { style: "solid", color: black, width: "2px" } },
  boxShadow: `inset 0 0 0 1px ${black}`,
});

const GoalTitle = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
});

const GoalDesc = styled.p({
  margin: { xy: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: 1.45,
});

const GoalZone = styled.span({
  marginTop: spacing.xs,
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: green,
});

const SessionHero = styled.div({
  marginTop: spacing.m,
  backgroundColor: white,
  borderRadius: "12px",
  padding: { xy: spacing.l },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const SessionName = styled.h3({
  margin: { xy: 0 },
  fontSize: fontSize.xl,
  fontWeight: fontWeight.heavy,
  color: black,
});

const SessionEffect = styled.p({
  margin: { top: spacing.xs, right: 0, bottom: spacing.m, left: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
});

const StatGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: spacing.m,
  ...media({ maxWidth: "520px" }, {
    gridTemplateColumns: "1fr",
  }),
});

const Stat = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
});

const StatLabel = styled.span({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const StatValue = styled.span({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
  lineHeight: 1.35,
});

const statAccent = styled({
  color: green,
  fontSize: fontSize.l,
});

const AltTitle = styled.h3({
  margin: { top: spacing.xl, right: 0, bottom: spacing.s, left: 0 },
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
});

const AltList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const AltRow = styled.button({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.m,
  width: "100%",
  textAlign: "left",
  cursor: "pointer",
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "10px",
  padding: { x: spacing.m, y: spacing.s },
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

const altRowActive = styled({
  border: { xy: { style: "solid", color: black, width: "2px" } },
});

const AltName = styled.div({
  fontWeight: fontWeight.bold,
  fontSize: fontSize.s,
  color: black,
});

const AltMeta = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
  marginTop: spacing.xxs,
});

const AltPick = styled.span({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: green,
  flexShrink: 0,
});

const PaceTable = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  marginTop: spacing.m,
});

const PaceRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.m,
  backgroundColor: white,
  borderRadius: "8px",
  padding: { x: spacing.m, y: spacing.s },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const PaceName = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
});

const PaceVal = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
  whiteSpace: "nowrap",
});

const FeelTitle = styled.h3({
  margin: { top: spacing.xl, right: 0, bottom: spacing.s, left: 0 },
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
});

const FeelTable = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const FeelRow = styled.div({
  padding: { y: spacing.xs },
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const FeelName = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
});

const FeelDesc = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
  marginTop: spacing.xxs,
});

const TipList = styled.ul({
  margin: { top: spacing.m, right: 0, bottom: 0, left: 0 },
  padding: { left: spacing.l },
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: 1.55,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const QuickCalc = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.s,
  marginTop: spacing.m,
});

const QuickChip = styled.div({
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "8px",
  padding: { x: spacing.s, y: spacing.xs },
  minWidth: "72px",
  textAlign: "center",
});

const QuickPct = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const QuickW = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
});
